/**
 * Calcula el estado general y el porcentaje de avance de un preparativo
 * basado en los estados de sus pasos individuales
 */

const ESTADOS_PASO = {
  NO_INICIADO: 'No iniciado',
  EN_GESTION: 'En gestión',
  CONFIRMADO: 'Confirmado',
  RECHAZADO: 'Rechazado'
};

const ESTADOS_GENERAL = {
  NO_INICIADO: 'No iniciado',
  EN_PROCESO: 'En proceso',
  COMPLETADO: 'Completado',
  CANCELADO: 'Cancelado'
};

const PASOS = [
  'estado_solicitud_embarque',
  'estado_propuesta_embarque',
  'estado_agendamiento_sag',
  'estado_coordinar_transporte',
  'estado_impresion_etiquetas',
  'estado_solicitud_dus'
];

/**
 * Calcula el estado general del preparativo
 * @param {Object} preparativo - Objeto preparativo con estados de pasos
 * @returns {string} Estado general
 */
export const calcularEstadoGeneral = (preparativo) => {
  if (!preparativo) return ESTADOS_GENERAL.NO_INICIADO;

  const estados = PASOS.map(paso => preparativo[paso] || ESTADOS_PASO.NO_INICIADO);

  // Si algún paso está rechazado, el preparativo está cancelado
  if (estados.some(estado => estado === ESTADOS_PASO.RECHAZADO)) {
    return ESTADOS_GENERAL.CANCELADO;
  }

  // Si todos los pasos están confirmados, el preparativo está completado
  if (estados.every(estado => estado === ESTADOS_PASO.CONFIRMADO)) {
    return ESTADOS_GENERAL.COMPLETADO;
  }

  // Si todos los pasos están en "No iniciado", el preparativo no ha iniciado
  if (estados.every(estado => estado === ESTADOS_PASO.NO_INICIADO)) {
    return ESTADOS_GENERAL.NO_INICIADO;
  }

  // En cualquier otro caso, está en proceso
  return ESTADOS_GENERAL.EN_PROCESO;
};

/**
 * Calcula el porcentaje de avance del preparativo
 * @param {Object} preparativo - Objeto preparativo con estados de pasos
 * @returns {number} Porcentaje de avance (0-100)
 */
export const calcularAvance = (preparativo) => {
  if (!preparativo) return 0;

  const estados = PASOS.map(paso => preparativo[paso] || ESTADOS_PASO.NO_INICIADO);
  
  // Contar pasos confirmados
  const pasosConfirmados = estados.filter(estado => estado === ESTADOS_PASO.CONFIRMADO).length;
  
  // Calcular porcentaje
  const porcentaje = Math.round((pasosConfirmados / PASOS.length) * 100);
  
  return porcentaje;
};

/**
 * Calcula tanto el estado como el avance
 * @param {Object} preparativo - Objeto preparativo con estados de pasos
 * @returns {Object} { estado, avance }
 */
export const calcularEstadoYAvance = (preparativo) => {
  return {
    estado: calcularEstadoGeneral(preparativo),
    avance: calcularAvance(preparativo)
  };
};

/**
 * Obtiene el color asociado a un estado general
 * @param {string} estado - Estado general
 * @returns {string} Clase CSS para el color
 */
export const getEstadoColor = (estado) => {
  switch (estado) {
    case ESTADOS_GENERAL.NO_INICIADO:
      return 'estado-no-iniciado';
    case ESTADOS_GENERAL.EN_PROCESO:
      return 'estado-en-proceso';
    case ESTADOS_GENERAL.COMPLETADO:
      return 'estado-completado';
    case ESTADOS_GENERAL.CANCELADO:
      return 'estado-cancelado';
    default:
      return 'estado-no-iniciado';
  }
};

export { ESTADOS_PASO, ESTADOS_GENERAL, PASOS };
